import { DepartmentResource } from '@/entities/Department/Department'
import { transformToDepartment } from '@/entities/Department/DepartmentTransformers'
import { DepartmentGroupResource } from '@/entities/DepartmentGroup/DepartmentGroup'
import { createUnknownErrorResult } from '@/entities/FetchResult'
import { FetchCollectionResult, FetchResultType } from '@/entities/FetchResult'
import { ShiftPlanningResource } from '@/entities/ShiftPlanning/ShiftPlanning'
import { transformToShiftPlanning } from '@/entities/ShiftPlanning/ShiftPlanningTransformers'
import { TemporaryWorkerResource } from '@/entities/TemporaryWorker/TemporaryWorker'
import { transformToTemporaryWorker } from '@/entities/TemporaryWorker/TemporaryWorkerTransformers'
import { ShiftIndexUserResource, UserResource } from '@/entities/User/User'
import { authorizedFetch } from '@/helpers/authorizedFetch'
import { formatDate } from '@/helpers/date'
import { reportError, reportMessage } from '@/helpers/errorReporting'
import { generateApiUrl } from '@/helpers/url'
import { getIncluded } from '@/japi/getIncluded'
import { getMultipleIncluded } from '@/japi/getMultipleIncluded'
import { isResourceCollectionDocument } from '@/japi/guards/isResourceCollectionDocument'
import { JapiDocument } from '@/japi/types/Document'
import { DateFormat } from '@/types'

import { ShiftIndex, ShiftResource } from './Shift'
import { transformToShiftIndex } from './ShiftTransformers'

let getShiftsIndexApiCallAbortController = new AbortController()

export const getShiftsIndexCall = async (
    startDate: Date,
    endDate: Date,
): Promise<FetchCollectionResult<ShiftIndex[], string>> => {
    getShiftsIndexApiCallAbortController.abort()
    getShiftsIndexApiCallAbortController = new AbortController()

    const includes = [
        'department',
        'department.group',
        'shiftPlannings',
        'shiftPlannings.user',
    ]

    try {
        const url = generateApiUrl({
            endpoint: '/api/v2/shifts',
            queryParams: {
                startDate: formatDate(startDate, DateFormat.apiParamsDateTime),
                endDate: formatDate(endDate, DateFormat.apiParamsDateTime),
                include: includes.join(','),
            },
        })

        const response = await authorizedFetch(url, { signal: getShiftsIndexApiCallAbortController.signal })

        const doc: JapiDocument = await response.json()

        if (! isResourceCollectionDocument<ShiftResource>(doc) || ! doc.data) {
            reportMessage('getShiftsIndexCall', `getShiftsIndexCall failed with status code ${response.status}`)

            return createUnknownErrorResult(response.status)
        }

        const shifts = doc.data.map(shift => {
            const departmentResource = getIncluded<DepartmentResource>(doc, shift, 'department')
            const shiftPlanningsResource = getMultipleIncluded<ShiftPlanningResource>(doc, shift, 'shiftPlannings')
            const temporaryWorkersResource = getMultipleIncluded<TemporaryWorkerResource>(doc, shift, 'temporaryWorkers')
            const previouslyPlannedUsersResource = getMultipleIncluded<UserResource>(doc, shift, 'previouslyPlannedUsers')

            if (! departmentResource || ! shiftPlanningsResource || ! temporaryWorkersResource) {
                throw new Error(`Shift ${shift.id} is missing its included department, shift plannings or temporary workers.`)
            }

            const departmentGroupResource = getIncluded<DepartmentGroupResource>(doc, departmentResource, 'group')
            const department = transformToDepartment(departmentResource, departmentGroupResource)

            const shiftPlannings = shiftPlanningsResource.map(shiftPlanningResource => {
                const userResource = getIncluded<ShiftIndexUserResource>(doc, shiftPlanningResource, 'user')

                if (! userResource) {
                    throw new Error(`Shift planning ${shiftPlanningResource.id} is missing its included user.`)
                }

                return transformToShiftPlanning(shiftPlanningResource, userResource)
            })

            const temporaryWorkers = temporaryWorkersResource.map(temporaryWorkerResource => transformToTemporaryWorker(temporaryWorkerResource, shift))

            return transformToShiftIndex(
                shift,
                department,
                shiftPlannings,
                temporaryWorkers,
                previouslyPlannedUsersResource,
            )
        })

        return {
            amountOfPages: 1,
            status: response.status,
            type: FetchResultType.Success,
            data: shifts,
        }
    } catch (error) {
        reportError('getShiftsIndexCall', error)

        return createUnknownErrorResult(500)
    }
}
